/**
 * InboxAllList — merged view of DMs + notifications sorted by time.
 */

import React, { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { formatDistanceToNow } from 'date-fns'
import { uz } from 'date-fns/locale'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@turnifly/shared/stores'
import { useConversations } from '@turnifly/shared/hooks'
import { useNotifications } from '@turnifly/shared/hooks'
import { AvatarWithBadge, getBadgeDisplay } from '@turnifly/shared/ui'
import { NotificationRow } from './notification-row'
import { LoadingSpinner } from '@turnifly/shared/ui'
import type { ConversationListItem } from '@turnifly/shared/api'
import type { NotificationItem } from '@turnifly/shared/api'

type MergedItem =
  | { kind: 'conversation'; data: ConversationListItem; timestamp: string }
  | { kind: 'notification'; data: NotificationItem; timestamp: string }

export const InboxAllList: React.FC = () => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const { data: conversations, isLoading: loadingConvs } = useConversations(user?.id)
  const { data: notifications, isLoading: loadingNotifs } = useNotifications(user?.id)

  const merged = useMemo((): MergedItem[] => {
    const items: MergedItem[] = []

    for (const conv of conversations ?? []) {
      const ts = conv.lastMessage?.created_at ?? ''
      if (ts) items.push({ kind: 'conversation', data: conv, timestamp: ts })
    }

    for (const notif of notifications ?? []) {
      items.push({ kind: 'notification', data: notif, timestamp: notif.created_at })
    }

    items.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
    return items
  }, [conversations, notifications])

  if (loadingConvs || loadingNotifs) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="md" />
      </div>
    )
  }

  if (merged.length === 0) {
    return (
      <div className="py-12 text-center text-text-hint text-sm">
        {t('inbox.empty')}
      </div>
    )
  }

  return (
    <div>
      {merged.map((item) => {
        if (item.kind === 'notification') {
          return <NotificationRow key={item.data.id} notification={item.data} />
        }

        const conv = item.data
        const badge = getBadgeDisplay(false, conv.participant?.badge_type)
        const timeAgo = formatDistanceToNow(new Date(item.timestamp), {
          addSuffix: false,
          locale: i18n.language === 'uz' ? uz : undefined,
        })
        const isMySender = conv.lastMessage?.sender_id === user?.id

        return (
          <button
            key={`conv-${conv.id}`}
            onClick={() => navigate(`/inbox/chat/${conv.id}`, {
              state: { participantName: conv.participant?.full_name ?? '' },
            })}
            className="flex items-center gap-3 w-full px-4 py-3 hover:bg-white/[0.02]
              transition-colors tap-highlight-transparent"
          >
            <AvatarWithBadge
              src={conv.participant?.avatar_url}
              fallback={(conv.participant?.full_name ?? '?').charAt(0)}
              size="md"
              badge={badge}
            />
            <div className="flex-1 min-w-0 text-left">
              <div className="flex items-center justify-between">
                <span className={`text-sm truncate ${conv.unreadCount > 0 ? 'font-medium text-text' : 'text-text/70'}`}>
                  {conv.participant?.full_name ?? t('dm.support')}
                </span>
                <span className="text-[11px] text-text-hint/60 shrink-0 ml-2">{timeAgo}</span>
              </div>
              {conv.lastMessage && (
                <p className="text-xs text-text-hint mt-0.5 truncate">
                  {isMySender ? `${t('dm.you')}: ` : ''}{conv.lastMessage.text}
                </p>
              )}
            </div>
            {conv.unreadCount > 0 && (
              <div className="min-w-[20px] h-5 flex items-center justify-center rounded-full bg-primary px-1.5 shrink-0">
                <span className="text-[10px] font-bold text-white">
                  {conv.unreadCount > 99 ? '99+' : conv.unreadCount}
                </span>
              </div>
            )}
          </button>
        )
      })}
    </div>
  )
}
